import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import { FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router';

import { GET_LOYALTY_MEMBER_BENEFITS } from 'store/queries';
import { Spinner } from 'admin-components/components';
import { getBookingJourneysURL } from 'utils/urls';
import { TIER_BENEFIT_STATUS_AVAILABLE, TIER_BENEFIT_STATUS_USED, TIER_BENEFIT_UPGRADE_NAME } from 'constants/tierBenefit';
import { INTERACTIVE_TOOLTIP_HIDE_DELAY_MS } from 'constants/tooltip';

import { RemainingContainer, RemainingLabel, TooltipContainer } from './MemberInfo.styles';

const TierBenefits = ({ pointsResetDate }) => {
  const { location } = useHistory();
  const { membershipNumber } = useParams();

  const endDate = moment(pointsResetDate);
  const startDate = moment(pointsResetDate).subtract(1, 'year').format('YYYY-MM-DD');

  const { data, loading, error } = useQuery(GET_LOYALTY_MEMBER_BENEFITS, {
    variables: {
      startDate,
      endDate: endDate.format('YYYY-MM-DD'),
      membershipNumber
    },
    fetchPolicy: 'network-only',
    skip: !membershipNumber && !pointsResetDate
  });

  const { loyaltyMemberBenefits } = data ?? {};

  let availableTierBenefitUpgradesCount = 0;
  let usedTierBenefitUpgrades = [];
  loyaltyMemberBenefits?.forEach((benefit) => {
    const { benefitName, benefitStatus } = benefit;

    if (benefitName === TIER_BENEFIT_UPGRADE_NAME) {
      if (benefitStatus === TIER_BENEFIT_STATUS_USED) {
        usedTierBenefitUpgrades.push(benefit);
      } else if (benefitStatus === TIER_BENEFIT_STATUS_AVAILABLE) {
        availableTierBenefitUpgradesCount += 1;
      }
    }
  });

  const handleReferenceClick = (reference) => () =>
    window.open(getBookingJourneysURL(reference, location.pathname.split('/')?.[2] ?? null));

  return (
    <RemainingContainer data-cy="tier-benefits-remaining">
      <FormattedMessage id="clubEurostar.tierBenefitUpgradesRemaining" tagName="span" />
      {loading ? (
        <Spinner type={'circular'} />
      ) : error ? (
        '-'
      ) : (
        <>
          <RemainingLabel data-for="bookings-with-tier-benefit-upgrades" data-tip="" data-cy="tier-benefit-upgrages-tooltip-label">
            {availableTierBenefitUpgradesCount}
          </RemainingLabel>
          <ReactTooltip
            className="tooltip"
            id="bookings-with-tier-benefit-upgrades"
            data-cy="bookings-with-tier-benefit-upgrades"
            place="bottom"
            effect="solid"
            delayHide={INTERACTIVE_TOOLTIP_HIDE_DELAY_MS}
          >
            <TooltipContainer>
              <FormattedMessage id="clubEurostar.tierBenefitUpgradesUpgradedBookings" />
              {usedTierBenefitUpgrades.length === 0 ? (
                <div>
                  <FormattedMessage
                    id="clubEurostar.tierBenefitUpgradesNoBookingsFound"
                    values={{ endTierDate: endDate.format('Do MMMM YYYY') }}
                  />
                </div>
              ) : (
                <ul>
                  {usedTierBenefitUpgrades.map(({ id, bookingReference }) => (
                    <li key={id} data-cy={`tier-benefit-booking-link-${bookingReference}`} onClick={handleReferenceClick(bookingReference)}>
                      {bookingReference}
                    </li>
                  ))}
                </ul>
              )}
            </TooltipContainer>
          </ReactTooltip>
        </>
      )}
    </RemainingContainer>
  );
};

TierBenefits.propTypes = {
  customerEmail: PropTypes.string,
  customerName: PropTypes.string,
  pointsResetDate: PropTypes.string
};

export default TierBenefits;
